import type {
  ISimpleIdStringNameEntity,
  IStripeLink,
} from '@booking-portal/types';
import type { IResponse } from '@booking-portal/types/api';
import { httpClient } from '@booking-portal/utils/fetch-utils';
import { isArray, isNumber, isString } from 'lodash-es';

type IDiffField<T> = keyof T;
type IDiffReturn<T> = Partial<Record<IDiffField<T>, unknown>>;

/**
 * returns a flat diff of two objects
 * @param currentData
 * @param prevData
 * @return { prop: [old, new] }
 */
export function getDiff<T>(
  currentData?: T | null,
  prevData?: T | null
): IDiffReturn<T> {
  const diff: IDiffReturn<T> = {};

  if (prevData && currentData) {
    const fieldSet = new Set<IDiffField<T>>([
      ...(Object.keys(prevData || {}) as IDiffField<T>[]),
      ...(Object.keys(currentData || {}) as IDiffField<T>[]),
    ]);
    const fields = Array.from(fieldSet);

    // TODO add typing
    return fields.reduce((all, field) => {
      if (prevData[field] !== currentData[field]) {
        all[field] = [prevData[field], currentData[field]];
      }

      return all;
    }, diff);
  }

  return diff;
}

export function toNumber<T>(
  num: number | string | null | undefined,
  fallback: T
): number | T {
  let parsed: number;

  if (num == null) {
    return fallback;
  }

  if (isNumber(num)) {
    parsed = num;
  } else {
    parsed = parseFloat(String(num));
  }

  return isNaN(parsed) ? fallback : parsed;
}

export function toBoolean(
  value: number | string | null | undefined
): undefined | boolean {
  if (value == null) {
    return undefined;
  }

  if (isString(value)) {
    if (value === 'true') {
      return true;
    }

    if (value === 'false') {
      return false;
    }
  }

  const num = toNumber(value, undefined);

  if (num != null) {
    return !!num;
  } else {
    return !!value;
  }
}

export function getImageFromAssets(assetPath: string): string {
  const url = `/src/assets/${assetPath.replace(/^\//, '')}`;
  try {
    return new URL(url, import.meta.url).href;
  } catch {
    return '';
  }
}

export function fetchAndDownloadStripeInvoice(url: string) {
  return httpClient
    .get<IResponse<IStripeLink>>(url)
    .then(({ data }) => {
      return data?.data?.invoicePdf;
    })
    .then((url) => {
      if (url) {
        downloadFromUrl(url);
      } else {
        return Promise.reject(new Error('error.invalidInvoicePdfUrl'));
      }
    });
}

export function downloadFromUrl(url: string) {
  if (url) {
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.download = 'download';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
}

/**
 * converts the given value to an array if it is not already an array
 */
export function toArray<T>(data: T | T[]): T[] {
  if (!isArray(data)) {
    return [data];
  }

  return data;
}

export function toSelectValues(
  items: ISimpleIdStringNameEntity[]
): { value: string; label: string }[] {
  return items.map((item) => ({
    value: item.id,
    label: item.name,
  }));
}
